import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Target, Eye, Building, Users, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SectionSeparator from '../components/ui/SectionSeparator';
import { companyInfo, mission, vision, companyProfile, whyChooseUs } from '../data/mock';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="About Us | SRAGCircle OPC - SEC Registered IT Systems Integrator Philippines"
        description="Learn about SRAGCircle OPC, a SEC-registered Philippine corporation specializing in IT systems integration, automation, and facilities management. Discover our mission, vision, and values."
        keywords="about SRAGCircle OPC, SEC registered corporation Philippines, IT company Metro Manila, systems integrator company profile"
        canonical="/about"
      />
      <Header />
      
      {/* Hero Section - Light Gray */}
      <section className="relative pt-32 pb-20 bg-gray-100 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#0066CC] font-semibold text-sm tracking-wider uppercase">About Us</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1a1a5c] mt-3 mb-6">
              About <span className="text-[#0066CC]">SRAGCircle</span> OPC
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              A SEC-registered Philippine corporation at the intersection of technology, engineering, and integrated support services.
            </p>
          </div>
        </div>
      </section>

      {/* Company Profile - Light Gray with Logo */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#0066CC]/10 rounded-xl flex items-center justify-center">
                  <Building className="w-6 h-6 text-[#0066CC]" />
                </div>
                <h2 className="text-3xl font-bold text-[#1a1a5c]">Company Profile</h2>
              </div>
              
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {companyProfile.overview}
              </p>
              
              <p className="text-gray-600 leading-relaxed mb-8">
                {companyProfile.leadership}
              </p>

              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-200">
                <h3 className="font-semibold text-[#1a1a5c] mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#0066CC]" />
                  Corporate Credentials
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#0066CC]" />
                    SEC-registered One-Person Corporation
                  </li>
                  <li className="flex items-center gap-3 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#0066CC]" />
                    Headquartered in Metro Manila, Philippines
                  </li>
                  <li className="flex items-center gap-3 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#0066CC]" />
                    35+ Years Combined Industry Experience
                  </li>
                </ul>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0066CC]/20 to-[#00C0FF]/20 rounded-3xl blur-[40px]" />
              <div className="relative bg-gray-100 rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
                <div className="p-8 flex items-center justify-center">
                  <img
                    src={companyInfo.logo}
                    alt={companyInfo.name}
                    className="w-full max-w-md mx-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Separator */}
        <SectionSeparator className="mt-12" />
      </section>

      {/* Business Lines - Light Background */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#0066CC] font-semibold text-sm tracking-wider uppercase">Business Nature</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a5c] mt-3 mb-4">
              Our Lines of Business
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Primary Business */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="inline-flex items-center gap-2 bg-[#0066CC] text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                Primary Business
              </div>
              <h3 className="text-xl font-bold text-[#1a1a5c] mb-4">Wholesale & Retail Trading</h3>
              <p className="text-gray-600 leading-relaxed">
                {companyProfile.primaryBusiness}
              </p>
            </div>

            {/* Secondary Businesses */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="inline-flex items-center gap-2 bg-[#00C0FF] text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                Secondary Businesses
              </div>
              <h3 className="text-xl font-bold text-[#1a1a5c] mb-4">Complementary Services</h3>
              <ul className="space-y-3">
                {companyProfile.secondaryBusinesses.map((business, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#0066CC] mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{business}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision - Dark */}
      <section className="py-20 bg-[#0a0a1a] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-[#0066CC]/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-[#00C0FF]/5 rounded-full blur-[80px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <div className="glass rounded-3xl p-8 md:p-10">
              <div className="w-16 h-16 bg-[#0066CC]/20 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#0066CC]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
              <p className="text-gray-400 leading-relaxed text-lg">
                "{mission}"
              </p>
            </div>

            {/* Vision */}
            <div className="glass rounded-3xl p-8 md:p-10">
              <div className="w-16 h-16 bg-[#00C0FF]/20 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-[#00C0FF]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Our Vision</h2>
              <p className="text-gray-400 leading-relaxed text-lg">
                "{vision}"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership & Expertise - Dark */}
      <section className="py-20 bg-[#0d0d1a] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-[#0066CC]/10 rounded-full blur-[80px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00C0FF] font-semibold text-sm tracking-wider uppercase">Our Strengths</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              Why Choose SRAGCircle OPC
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {whyChooseUs.map((item, index) => (
              <div
                key={item.id}
                className="flex items-start gap-4 p-6 glass rounded-2xl hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#0066CC]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-[#00C0FF]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#0066CC] to-[#004499]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Discover how our integrated solutions can transform your operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services">
              <Button className="bg-white text-[#0066CC] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl">
                View Our Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-xl">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
